import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  TriasCard, CardAttribute, DIFFICULTY_CONFIG,
  createGame, isValidSet, hasValidSet,
  processFoundSet, addThreeCards,
} from '../../utils/triasEngine';
import VictoryModal from '../../components/game/VictoryModal';
import HowToPlayModal from '../../components/game/HowToPlayModal';
import { useGameProgress } from '../../hooks/useGameProgress';
import { useSessionContext } from '../../hooks/useSessionContext';
import SessionBanner from '../../components/game/SessionBanner';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function explainSet(
  cards: [TriasCard, TriasCard, TriasCard],
  activeAttributes: CardAttribute[],
): string {
  const labels: Record<CardAttribute, string> = {
    symbol: 'Símbolo', color: 'Color', amount: 'Cantidad', fill: 'Relleno',
  };
  return activeAttributes.map(attr => {
    const allSame = new Set(cards.map(c => c[attr])).size === 1;
    return `${labels[attr]}: ${allSame ? 'iguales' : 'distintos'}`;
  }).join(' · ');
}

// ─── Card component ───────────────────────────────────────────────────────────

interface CardProps {
  card: TriasCard;
  selected: boolean;
  feedback: 'success' | 'error' | null;
  disabled: boolean;
  onClick: () => void;
  cardPx?: number;
}

function Card({ card, selected, feedback, disabled, onClick, cardPx }: CardProps) {
  let ring = '';
  let bg   = 'bg-white';

  if (feedback === 'success') {
    ring = 'ring-4 ring-green-400 ring-offset-1';
    bg   = 'bg-green-50';
  } else if (feedback === 'error') {
    ring = 'ring-4 ring-red-400 ring-offset-1';
    bg   = 'bg-red-50';
  } else if (selected) {
    ring = 'ring-4 ring-yellow-400 ring-offset-1';
    bg   = 'bg-yellow-50';
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{ aspectRatio: '3/2', ...(cardPx ? { width: cardPx } : { width: '100%' }) }}
      className={`
        ${bg} ${ring}
        rounded-xl shadow-md transition-all duration-150 overflow-hidden relative
        ${selected && feedback === null ? 'scale-105 shadow-xl' : ''}
        ${!disabled ? 'hover:shadow-xl active:scale-95 cursor-pointer' : 'cursor-default'}
        ${feedback === 'error' ? 'animate-shake' : ''}
      `}
    >
      <img
        src={card.image}
        alt={card.id.replace(/_/g, ' ')}
        style={{
          position: 'absolute',
          width: cardPx ? cardPx * (2/3) : '66.67%',
          height: cardPx ? cardPx : '150%',
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%) rotate(90deg)',
        }}
        draggable={false}
        onError={(e) => {
          (e.target as HTMLImageElement).style.display = 'none';
        }}
      />
    </button>
  );
}

// ─── Main game ────────────────────────────────────────────────────────────────

const DIFFICULTIES = [
  { orden: 1, nombre: 'Sinapsis', icono: '⚡' },
  { orden: 2, nombre: 'Cerebro',  icono: '🧩' },
  { orden: 3, nombre: 'Genio',    icono: '💡' },
  { orden: 4, nombre: 'Einstein', icono: '🎓' },
];

export default function TriasGame() {
  const { dificultad } = useParams<{ dificultad: string }>();
  const navigate = useNavigate();

  const dif = parseInt(dificultad || '1');
  const config = DIFFICULTY_CONFIG[dif] ?? DIFFICULTY_CONFIG[1];

  const { saveProgress: saveGameProgress } = useGameProgress('trias', dif);
  const { isInSession, sessionType, saveSessionResult } = useSessionContext();

  const [board, setBoard]               = useState<TriasCard[]>([]);
  const [deck, setDeck]                 = useState<TriasCard[]>([]);
  const [selected, setSelected]         = useState<number[]>([]);
  const [setsFound, setSetsFound]       = useState(0);
  const [errors, setErrors]             = useState(0);
  const [elapsedTime, setElapsedTime]   = useState(0);
  const [isComplete, setIsComplete]     = useState(false);
  const [showVictory, setShowVictory]   = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [feedbackType, setFeedbackType]     = useState<'success' | 'error' | null>(null);
  const [feedbackIdx, setFeedbackIdx]       = useState<number[]>([]);
  const [infoMsg, setInfoMsg]               = useState('');
  const [pendingContinue, setPendingContinue] = useState<[number, number, number] | null>(null);

  const boardAreaRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!boardAreaRef.current) return;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries)
        setContainerSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    ro.observe(boardAreaRef.current);
    return () => ro.disconnect();
  }, []);

  const isAnimating = feedbackType !== null;

  // ─── Init ─────────────────────────────────────────────────────────────────

  const startNewGame = (difficulty = dif) => {
    const { board: b, deck: d } = createGame(difficulty);
    setBoard(b);
    setDeck(d);
    setSelected([]);
    setSetsFound(0);
    setErrors(0);
    setElapsedTime(0);
    setIsComplete(false);
    setShowVictory(false);
    setFeedbackType(null);
    setFeedbackIdx([]);
    setInfoMsg('');
    setPendingContinue(null);
  };

  useEffect(() => { startNewGame(dif); }, [dif]);

  // ─── Timer ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (isComplete || pendingContinue) return;
    const t = setInterval(() => setElapsedTime(s => s + 1), 1000);
    return () => clearInterval(t);
  }, [isComplete, pendingContinue]);

  // ─── Card click ───────────────────────────────────────────────────────────

  const handleCardClick = (idx: number) => {
    if (isAnimating || isComplete || pendingContinue) return;
    setInfoMsg('');

    if (selected.includes(idx)) {
      setSelected(s => s.filter(i => i !== idx));
      return;
    }
    if (selected.length >= 3) return;

    const newSelected = [...selected, idx];
    setSelected(newSelected);

    if (newSelected.length < 3) return;

    // Validate the triplet
    const triple = newSelected.map(i => board[i]) as [TriasCard, TriasCard, TriasCard];
    const valid  = isValidSet(triple, config.activeAttributes);

    setFeedbackIdx(newSelected);
    setFeedbackType(valid ? 'success' : 'error');

    if (valid) {
      setSetsFound(s => s + 1);
      setPendingContinue(newSelected as [number, number, number]);
    } else {
      setErrors(e => e + 1);
      setTimeout(() => {
        setFeedbackType(null);
        setFeedbackIdx([]);
        setSelected([]);
      }, 600);
    }
  };

  // ─── No veo más ───────────────────────────────────────────────────────────

  const handleNoMas = () => {
    if (isAnimating || isComplete) return;
    setInfoMsg('');

    if (hasValidSet(board, config.activeAttributes)) {
      setInfoMsg('¡Aún hay al menos una tríada válida! Sigue buscando.');
      setTimeout(() => setInfoMsg(''), 3000);
      return;
    }

    if (deck.length > 0) {
      let nb = board;
      let nd = deck;
      while (!hasValidSet(nb, config.activeAttributes) && nd.length > 0) {
        ({ board: nb, deck: nd } = addThreeCards(nb, nd));
      }
      setBoard(nb);
      setDeck(nd);

      if (!hasValidSet(nb, config.activeAttributes) && nd.length === 0) {
        setIsComplete(true);
        saveGameProgress({ tiempo: elapsedTime, movimientos: setsFound });
        if (isInSession) saveSessionResult({ tiempo: elapsedTime, movimientos: setsFound, completado: true });
        setTimeout(() => setShowVictory(true), 400);
      }
    } else {
      setIsComplete(true);
      saveGameProgress({ tiempo: elapsedTime, movimientos: setsFound });
      if (isInSession) saveSessionResult({ tiempo: elapsedTime, movimientos: setsFound, completado: true });
      setTimeout(() => setShowVictory(true), 400);
    }
  };

  // ─── Continuar tras tríada ────────────────────────────────────────────────

  const handleContinue = () => {
    if (!pendingContinue) return;

    setFeedbackType(null);
    setFeedbackIdx([]);
    setSelected([]);
    setPendingContinue(null);

    let { board: nb, deck: nd } = processFoundSet(board, deck, pendingContinue);

    while (!hasValidSet(nb, config.activeAttributes) && nd.length > 0) {
      ({ board: nb, deck: nd } = addThreeCards(nb, nd));
    }

    setBoard(nb);
    setDeck(nd);

    if (!hasValidSet(nb, config.activeAttributes) && nd.length === 0) {
      setIsComplete(true);
      saveGameProgress({ tiempo: elapsedTime, movimientos: setsFound });
      if (isInSession) saveSessionResult({ tiempo: elapsedTime, movimientos: setsFound, completado: true });
      setTimeout(() => setShowVictory(true), 400);
    }
  };

  // ─── Helpers ──────────────────────────────────────────────────────────────

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  const accuracy = setsFound + errors > 0
    ? Math.round(setsFound / (setsFound + errors) * 100)
    : 100;

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="h-full bg-gradient-to-br from-purple-600 to-blue-600 flex flex-col">
      {isInSession && sessionType && <SessionBanner sessionType={sessionType as 'presencial' | 'remota'} />}

      {/* Compact top bar */}
      <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="text-white font-bold text-base whitespace-nowrap">🧠 Tríadas</span>
        <div className="w-px h-5 bg-white/30 hidden sm:block" />

        {/* Difficulty selector */}
        <div className="flex gap-1 flex-wrap">
          {DIFFICULTIES.map(d => (
            <button
              key={d.orden}
              onClick={() => {
                if (dif === d.orden) startNewGame(d.orden);
                else navigate(`/juegos/trias/${d.orden}`);
              }}
              className={`px-2 py-1 rounded-md text-xs font-semibold transition-all ${
                dif === d.orden ? 'bg-white text-purple-700' : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              {d.icono} <span className="hidden sm:inline">{d.nombre}</span>
            </button>
          ))}
        </div>

        <div className="flex-1" />

        {/* Stats */}
        <div className="flex items-center gap-2 text-white/90 text-xs font-medium">
          <span title="Tríadas encontradas">✅ {setsFound}</span>
          <span title="Errores">❌ {errors}</span>
          <span title="Precisión" className="hidden sm:inline">🎯 {accuracy}%</span>
          <span title="Tiempo">⏱ {formatTime(elapsedTime)}</span>
          <span title="Cartas restantes" className="hidden sm:inline">🃏 {deck.length}</span>
        </div>

        <div className="flex gap-1 items-center ml-1">
          {/* No veo más */}
          <button
            onClick={handleNoMas}
            disabled={isComplete || isAnimating}
            title="No veo ninguna tríada"
            className="px-2 py-1 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all text-xs font-semibold whitespace-nowrap"
          >
            No veo más
          </button>
          {/* Nueva partida */}
          <button
            onClick={() => startNewGame()}
            title="Nueva partida"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 8v8M8 12h8"/></svg>
          </button>
          {/* Cómo se juega */}
          <button
            onClick={() => setShowHowToPlay(true)}
            title="Cómo se juega"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          </button>
        </div>
      </div>

      {/* Info message */}
      {infoMsg && (
        <div className="mx-3 mt-2 p-2 bg-blue-100 border border-blue-300 text-blue-800 rounded-lg text-sm text-center">
          {infoMsg}
        </div>
      )}

      {/* Active attributes indicator */}
      <div className="px-3 pt-1.5 pb-0 flex gap-2 justify-center flex-wrap">
        {(['symbol', 'color', 'amount', 'fill'] as const).map(attr => {
          const labels: Record<string, string> = {
            symbol: 'Símbolo', color: 'Color', amount: 'Cantidad', fill: 'Relleno',
          };
          const active = config.activeAttributes.includes(attr);
          return (
            <span
              key={attr}
              className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                active
                  ? 'bg-white/30 text-white'
                  : 'bg-black/20 text-white/40 line-through'
              }`}
            >
              {labels[attr]}
            </span>
          );
        })}
      </div>

      {/* Found set banner */}
      {pendingContinue && (
        <div className="mx-3 mt-2 p-3 bg-green-500 rounded-xl shadow-lg flex flex-col sm:flex-row items-center gap-3">
          <div className="flex-1 text-center sm:text-left">
            <div className="text-white font-bold text-lg">¡Tríada encontrada! ✅</div>
            <div className="text-green-100 text-sm mt-0.5">
              {explainSet(
                pendingContinue.map(i => board[i]) as [TriasCard, TriasCard, TriasCard],
                config.activeAttributes,
              )}
            </div>
          </div>
          <button
            onClick={handleContinue}
            className="px-6 py-2.5 bg-white text-green-700 font-bold rounded-lg shadow hover:bg-green-50 active:scale-95 transition-all text-base whitespace-nowrap"
          >
            Continuar →
          </button>
        </div>
      )}

      {/* Board */}
      {(() => {
        const cols = containerSize.width >= 640 ? 4 : 3;
        const rows = Math.ceil(board.length / cols);
        const gap = 8;
        const cw = containerSize.width - 16;
        const ch = containerSize.height - 16;
        const cardPx = cw > 0 && ch > 0
          ? Math.floor(Math.min(
              (cw - gap * (cols - 1)) / cols,
              (ch - gap * (rows - 1)) / rows * (3 / 2)
            ))
          : undefined;
        return (
          <div ref={boardAreaRef} className="flex-1 min-h-0 p-2 sm:p-3 flex items-center justify-center overflow-hidden">
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 sm:gap-3 justify-items-center">
              {board.map((card, i) => (
                <Card
                  key={`${card.id}-${i}`}
                  card={card}
                  selected={selected.includes(i) && !feedbackIdx.includes(i)}
                  feedback={feedbackIdx.includes(i) ? feedbackType : null}
                  disabled={isAnimating || isComplete || !!pendingContinue}
                  onClick={() => handleCardClick(i)}
                  cardPx={cardPx}
                />
              ))}
            </div>
          </div>
        );
      })()}

      <VictoryModal
        isOpen={showVictory}
        onClose={() => { setShowVictory(false); startNewGame(); }}
        stats={{ tiempo: elapsedTime, movimientos: setsFound }}
      />

      <HowToPlayModal
        isOpen={showHowToPlay}
        onClose={() => setShowHowToPlay(false)}
        gameTitle="Tríadas"
        gameIcon="🧠"
        objective="Encuentra grupos de 3 cartas que formen una tríada válida. Para cada atributo activo, los 3 valores deben ser todos iguales o todos diferentes."
        rules={[
          'Cada carta tiene 4 atributos: símbolo, color, relleno y cantidad.',
          'Tres cartas forman una tríada si, para cada atributo activo, los valores son todos iguales O todos diferentes.',
          'Si dos cartas comparten un valor y la tercera es diferente, la tríada NO es válida.',
          'Al seleccionar 3 cartas se valida automáticamente: verde = acierto, rojo = error.',
          'Si no ves ninguna tríada, pulsa «No veo más». Si hay una, el juego te lo dirá. Si no hay, recibirás 3 cartas nuevas.',
          'La partida termina cuando se agotan las cartas y no quedan tríadas posibles.',
        ]}
        howToPlay="Toca tres cartas para seleccionarlas. Las cartas seleccionadas se resaltan en amarillo. El nivel determina cuántos atributos cuentan para la validación — Sinapsis solo evalúa símbolo y color, Genio y Einstein usan los 4."
        author="Inspirado en SET® de Marsha Falco"
      />
    </div>
  );
}
